import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV } from "@/data/site";
import { useCart } from "@/lib/cart";
import { EASE } from "@/lib/api";
import { HolidayBanner } from "@/components/HolidayBanner";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Header = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setMenu(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-espresso/10" : "bg-transparent border-transparent"
      }`}
    >
      {!menu && <HolidayBanner />}
      <div className={`mx-auto max-w-[1440px] px-5 md:px-10 flex items-center justify-between gap-6 transition-[padding] duration-500 ${scrolled ? "py-3" : "py-5"}`}>
        <Link to="/" data-testid="header-logo-link" className="relative z-50">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${slug(n.label)}`}
              className={({ isActive }) => `link-u text-[12px] uppercase tracking-[0.22em] text-espresso pb-1 ${isActive ? "active" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 relative z-50">
          <button
            data-testid="cart-drawer-toggle"
            onClick={() => setOpen(true)}
            className="relative p-2.5 text-espresso hover:text-gold transition-colors"
            aria-label="Open bag"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.4} />
            <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-espresso text-cream text-[10px] flex items-center justify-center">
              {count}
            </span>
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setMenu((m) => !m)} className="lg:hidden p-2.5 text-espresso" aria-label="Menu">
            {menu ? <X className="w-5 h-5" strokeWidth={1.4} /> : <Menu className="w-5 h-5" strokeWidth={1.4} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="lg:hidden fixed inset-0 bg-cream z-40 px-6 pt-32 flex flex-col"
          >
            {NAV.map((n, i) => (
              <motion.div key={n.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.06, ease: EASE, duration: 0.7 }}>
                <Link to={n.to} data-testid={`mobile-nav-${slug(n.label)}`} className="block font-display text-5xl py-2 text-espresso">
                  {n.label}
                </Link>
              </motion.div>
            ))}
            <p className="eyebrow mt-auto mb-10">Clifton Heights · Pennsylvania</p>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
